import { parseTimelineDate, addMonths, phaseShortLabel, formatTimelineDate } from './ongoingTrialsTimeline';

export type TrialTrackerEventType =
  | 'NEW_TRIAL'
  | 'RESULTS'
  | 'ENROLLMENT'
  | 'STATUS'
  | 'PHASE'
  | 'DATE'
  | 'ARM'
  | 'DESIGN'
  | 'SPONSOR'
  | 'CURRENT';

export interface TrialTrackerDesignation {
  code: string;
  agency: string;
  granted_date?: string | null;
}

export interface TrialTrackerEvent {
  id: number | string;
  event_type: TrialTrackerEventType;
  headline: string;
  from_value?: string | null;
  to_value?: string | null;
  reason?: string | null;
  is_priority: boolean;
  source: string;
  detected_at: string | null;
  read: boolean;
  favorited: boolean;
  nct_id: string;
  trial_name?: string | null;
  full_title?: string | null;
  phase?: string | null;
  status?: string | null;
  sponsor?: string | null;
  indication?: string | null;
  indication_id?: number | null;
  indication_name?: string | null;
  asset_name?: string | null;
  enrollment?: string | number | null;
  study_start_date?: string | null;
  primary_completion_date?: string | null;
  study_completion_date?: string | null;
  designations: TrialTrackerDesignation[];
}

export interface TrialTrackerTrial {
  nct_id: string;
  trial_name?: string | null;
  full_title?: string | null;
  phase?: string | null;
  status?: string | null;
  sponsor?: string | null;
  enrollment?: string | null;
  study_start_date?: string | null;
  primary_completion_date?: string | null;
  study_completion_date?: string | null;
  indication?: string | null;
  indication_id?: number | null;
  indication_name?: string | null;
  asset_name?: string | null;
  designations: TrialTrackerDesignation[];
  favorited: boolean;
}

export interface TrialTrackerIndication {
  id: number;
  name: string;
  cancer_type?: string | null;
  trial_count: number;
  last_updated_at: string | null;
  last_scraped_at?: string | null;
}

export const UNCATEGORIZED_CANCER_TYPE = 'Other / uncategorized';

/** Group indications under SoC cancer_type labels for the browse panel. */
export function groupIndicationsByCancerType(
  indications: TrialTrackerIndication[],
): { cancerType: string; items: TrialTrackerIndication[] }[] {
  const map = new Map<string, TrialTrackerIndication[]>();
  for (const ind of indications) {
    const key = (ind.cancer_type || '').trim() || UNCATEGORIZED_CANCER_TYPE;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(ind);
  }
  const groups = Array.from(map.entries()).map(([cancerType, items]) => ({
    cancerType,
    items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
  }));
  groups.sort((a, b) => {
    if (a.cancerType === UNCATEGORIZED_CANCER_TYPE) return 1;
    if (b.cancerType === UNCATEGORIZED_CANCER_TYPE) return -1;
    return a.cancerType.localeCompare(b.cancerType);
  });
  return groups;
}

export type TrialTrackerUpdateKind = 'status_event' | 'readout';

export interface TrialTrackerUpdate {
  kind: TrialTrackerUpdateKind;
  id: number | string;
  event_type: string;
  headline: string;
  from_value?: string | null;
  to_value?: string | null;
  reason?: string | null;
  is_priority?: boolean;
  source?: string | null;
  source_url?: string | null;
  source_name?: string | null;
  timing_start?: string | null;
  timing_end?: string | null;
  detected_at: string | null;
}

export interface IndicationSearchResult {
  nct_id: string;
  trial_name?: string | null;
  overall_status?: string | null;
  phases?: string | null;
  sponsor?: string | null;
  conditions: string[];
  already_in_db: boolean;
  already_reviewed: boolean;
  already_tracked_by_me: boolean;
}

export type TrialTrackerSearchMode = 'all' | 'asset' | 'indication' | 'nct';

export interface TrialTrackerSearchResult {
  nct_id: string;
  trial_name?: string | null;
  full_title?: string | null;
  phases?: string | null;
  overall_status?: string | null;
  status?: string | null;
  sponsor?: string | null;
  organization_full_name?: string | null;
  indication_details?: string | null;
  asset_name?: string | null;
  indication_name?: string | null;
  study_start_date?: string | null;
  primary_completion_date?: string | null;
  study_completion_date?: string | null;
  completion_date?: string | null;
  enrollment?: string | null;
  brief_summary?: string | null;
  primary_outcome_measures?: string[] | null;
  intervention_names?: string[] | null;
  conditions?: string[] | null;
  in_database?: boolean;
  from_ctgov?: boolean;
}

export interface TrackerGroup<T> {
  name: string;
  items: T[];
}

/** Case-insensitive sponsor match for in-view company filtering. */
export function normalizeSponsorKey(sponsor?: string | null): string {
  return (sponsor || '').trim().toLowerCase();
}

export function sponsorsMatch(a?: string | null, b?: string | null): boolean {
  const ka = normalizeSponsorKey(a);
  const kb = normalizeSponsorKey(b);
  return Boolean(ka && kb && ka === kb);
}

/** One-line summary for drawer / feed update previews. */
export function formatTrialTrackerUpdateLine(u: Pick<TrialTrackerUpdate, 'kind' | 'headline' | 'from_value' | 'to_value'>): string {
  const base = (u.headline || '').trim();
  if (u.from_value && u.to_value && u.from_value !== u.to_value) {
    return `${base} (${u.from_value} → ${u.to_value})`;
  }
  if (u.to_value) return `${base} (${u.to_value})`;
  return base;
}

/** Common oncology abbreviation <-> long-form groups, so typing "non small cell" finds "NSCLC",
 * "colorectal" finds "CRC", etc. Each entry is pre-normalized (lowercase, no punctuation). Deliberately
 * excludes short common-word abbreviations (e.g. "ALL" for acute lymphoblastic leukemia) that would
 * over-match everything. */
const INDICATION_SYNONYM_GROUPS: string[][] = [
  ['nsclc', 'non small cell lung cancer', 'non small cell lung', 'non small cell'],
  ['sclc', 'small cell lung cancer', 'small cell lung'],
  ['crc', 'colorectal cancer', 'colorectal', 'colon cancer', 'rectal cancer'],
  ['hnscc', 'head and neck squamous cell carcinoma', 'head and neck cancer', 'head and neck'],
  ['tnbc', 'triple negative breast cancer', 'triple negative breast'],
  ['pdac', 'pancreatic ductal adenocarcinoma', 'pancreatic cancer', 'pancreatic'],
  ['mibc', 'muscle invasive bladder cancer', 'muscle invasive bladder'],
  ['nmibc', 'non muscle invasive bladder cancer', 'non muscle invasive bladder'],
  ['uc', 'urothelial carcinoma', 'urothelial cancer', 'urothelial'],
  ['mcrpc', 'metastatic castration resistant prostate cancer', 'castration resistant prostate cancer', 'castration resistant prostate'],
  ['hspc', 'hormone sensitive prostate cancer', 'hormone sensitive prostate'],
  ['cll', 'chronic lymphocytic leukemia'],
  ['sll', 'small lymphocytic lymphoma'],
  ['rcc', 'renal cell carcinoma', 'kidney cancer', 'renal cancer'],
  ['mm', 'multiple myeloma'],
  ['aml', 'acute myeloid leukemia'],
  ['dlbcl', 'diffuse large b cell lymphoma', 'diffuse large b cell'],
  ['gist', 'gastrointestinal stromal tumor'],
  ['hcc', 'hepatocellular carcinoma', 'liver cancer'],
  ['nets', 'neuroendocrine tumors', 'neuroendocrine tumor', 'neuroendocrine'],
  ['mds', 'myelodysplastic syndrome'],
  ['mpn', 'myeloproliferative neoplasm'],
  ['msi h', 'microsatellite instability high', 'microsatellite instability'],
  ['mss', 'microsatellite stable'],
  ['dmmr', 'mismatch repair deficient', 'deficient mismatch repair'],
  ['egfr', 'epidermal growth factor receptor'],
  ['alk', 'anaplastic lymphoma kinase'],
  ['ros1', 'ros proto oncogene 1'],
  ['her2', 'human epidermal growth factor receptor 2'],
  ['pdl1', 'pd l1', 'programmed death ligand 1'],
];

function normalizeIndicationText(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function expandIndicationSearchText(name: string): string {
  const normalized = normalizeIndicationText(name);
  const extra: string[] = [];
  for (const group of INDICATION_SYNONYM_GROUPS) {
    if (group.some((phrase) => normalized.includes(phrase))) extra.push(...group);
  }
  return extra.length ? `${normalized} ${extra.join(' ')}` : normalized;
}

const LINE_OF_THERAPY_PREFIXES = /^(1l|2l\+?|3l\+?|4l\+?|adjuvant|neoadjuvant|perioperative|maintenance|first line|second line|third line|relapsed refractory|r r)\s+/;

function stripLineOfTherapy(name: string): string {
  let normalized = normalizeIndicationText(name);
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const next = normalized.replace(LINE_OF_THERAPY_PREFIXES, '');
    if (next === normalized) break;
    normalized = next;
  }
  return normalized;
}

/** Best-effort: several plain-language phrasings for a terse canonical indication label
 * ("1L mCRPC" -> ["metastatic castration resistant prostate cancer", "mcrpc"]), for the
 * "Search new indication" panel. CT.gov's condition search is picky about jargon — searching
 * the raw label literally ("1L mCRPC") often returns almost nothing (verified: 1 result vs.
 * 25+ for either variant below), so offer ready-to-try alternatives rather than one guess. */
export function suggestSearchQueryVariants(name: string): string[] {
  const stripped = stripLineOfTherapy(name);
  const variants: string[] = [];
  for (const group of INDICATION_SYNONYM_GROUPS) {
    if (group.some((phrase) => stripped === phrase || stripped.includes(phrase))) {
      const longForm = group[1] || group[0];
      const expanded = stripped.replace(group[0], longForm).trim() || longForm;
      variants.push(expanded, group[0]);
      break;
    }
  }
  if (!variants.includes(stripped)) variants.push(stripped);
  return Array.from(new Set(variants)).filter(Boolean).slice(0, 3);
}

/** First (best) suggestion — kept for callers that just want one pre-filled query. */
export function suggestSearchQueryForIndication(name: string): string {
  return suggestSearchQueryVariants(name)[0] || normalizeIndicationText(name);
}

/** Synonym-aware match: "non small cell" finds "NSCLC", "nsclc" finds "Non-Small Cell Lung Cancer". */
export function matchesIndicationQuery(name: string, query: string): boolean {
  const q = normalizeIndicationText(query);
  if (!q) return true;
  const tokens = q.split(' ').filter(Boolean);
  const haystack = expandIndicationSearchText(name);
  return tokens.every((t) => haystack.includes(t));
}

/** Group items by indication_name, falling back to "Other / unspecified", sorted alphabetically. */
export function groupByIndication<T extends {
  indication_name?: string | null;
  detected_at?: string | null;
  trial_name?: string | null;
  nct_id?: string;
}>(
  items: T[],
): TrackerGroup<T>[] {
  const map = new Map<string, T[]>();
  items.forEach((item) => {
    const key = item.indication_name || 'Other / unspecified';
    const list = map.get(key);
    if (list) list.push(item);
    else map.set(key, [item]);
  });
  return Array.from(map.entries())
    .map(([name, groupItems]) => ({
      name,
      items: [...groupItems].sort((a, b) => {
        const byDate = (b.detected_at || '').localeCompare(a.detected_at || '');
        if (byDate !== 0) return byDate;
        const an = (a.trial_name || a.nct_id || '').toString();
        const bn = (b.trial_name || b.nct_id || '').toString();
        return an.localeCompare(bn);
      }),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function isCurrentFeedEvent(
  event: Pick<TrialTrackerEvent, 'event_type' | 'source'>,
): boolean {
  return event.event_type === 'CURRENT' || event.source === 'snapshot';
}

export function isPersistedFeedEventId(id: TrialTrackerEvent['id']): id is number {
  return typeof id === 'number' && id > 0;
}

/** Baseline card from current trial state — not a change alert and not persisted. */
export function trialToCurrentEvent(trial: TrialTrackerTrial): TrialTrackerEvent {
  return {
    id: `current:${trial.indication_id ?? 'x'}:${trial.nct_id}`,
    event_type: 'CURRENT',
    headline: '',
    is_priority: false,
    source: 'snapshot',
    detected_at: null,
    read: true,
    favorited: Boolean(trial.favorited),
    nct_id: trial.nct_id,
    trial_name: trial.trial_name,
    full_title: trial.full_title,
    phase: trial.phase,
    status: trial.status,
    sponsor: trial.sponsor,
    enrollment: trial.enrollment,
    study_start_date: trial.study_start_date,
    primary_completion_date: trial.primary_completion_date,
    study_completion_date: trial.study_completion_date,
    indication: trial.indication,
    indication_id: trial.indication_id,
    indication_name: trial.indication_name,
    asset_name: trial.asset_name,
    designations: trial.designations || [],
  };
}

function feedRowKey(indicationId: number | null | undefined, nctId: string): string {
  return `${indicationId ?? ''}::${nctId}`;
}

/** One row per counted trial: keep real alerts, add a CURRENT card only when that
 * indication+NCT pair is otherwise missing from the feed. */
export function mergeFeedWithCurrentTrials(
  events: TrialTrackerEvent[],
  trials: TrialTrackerTrial[],
): TrialTrackerEvent[] {
  const seen = new Set(events.map((e) => feedRowKey(e.indication_id, e.nct_id)));
  const extra: TrialTrackerEvent[] = [];
  for (const trial of trials) {
    if (!trial.nct_id) continue;
    const key = feedRowKey(trial.indication_id, trial.nct_id);
    if (seen.has(key)) continue;
    seen.add(key);
    extra.push(trialToCurrentEvent(trial));
  }
  return extra.length ? [...events, ...extra] : events;
}

export const TRIAL_TRACKER_EVENT_TYPES: TrialTrackerEventType[] = [
  'NEW_TRIAL', 'RESULTS', 'ENROLLMENT', 'STATUS', 'PHASE', 'DATE', 'ARM', 'DESIGN', 'SPONSOR',
];

export const TYPE_META: Record<TrialTrackerEventType, { label: string; glyph: string; bg: string; fg: string; cat: string }> = {
  NEW_TRIAL: { label: 'NEW TRIAL', glyph: '+', bg: 'bg-tracker-newBg', fg: 'text-tracker-newFg', cat: 'new' },
  RESULTS: { label: 'RESULTS', glyph: '▣', bg: 'bg-tracker-resultsBg', fg: 'text-tracker-resultsFg', cat: 'results' },
  ENROLLMENT: { label: 'ENROLLMENT', glyph: '↑', bg: 'bg-tracker-enrollBg', fg: 'text-tracker-enrollFg', cat: 'enroll' },
  STATUS: { label: 'STATUS', glyph: '→', bg: 'bg-tracker-statusBg', fg: 'text-tracker-statusFg', cat: 'status' },
  PHASE: { label: 'PHASE', glyph: '△', bg: 'bg-tracker-phaseBg', fg: 'text-tracker-phaseFg', cat: 'status' },
  DATE: { label: 'DATE', glyph: '◷', bg: 'bg-tracker-dateBg', fg: 'text-tracker-dateFg', cat: 'dates' },
  ARM: { label: 'NEW ARM', glyph: '⊞', bg: 'bg-tracker-designBg', fg: 'text-tracker-designFg', cat: 'design' },
  DESIGN: { label: 'DESIGN', glyph: '◨', bg: 'bg-tracker-designBg', fg: 'text-tracker-designFg', cat: 'design' },
  SPONSOR: { label: 'SPONSOR', glyph: '⇄', bg: 'bg-tracker-sponsorBg', fg: 'text-tracker-sponsorFg', cat: 'sponsor' },
  CURRENT: { label: 'CURRENT', glyph: '○', bg: 'bg-tracker-defaultBg', fg: 'text-tracker-defaultFg', cat: 'current' },
};

export const READOUT_UPDATE_META = {
  label: 'READOUT',
  glyph: '◉',
  bg: 'bg-orange-50',
  fg: 'text-orange-700',
  cat: 'readout',
};

export function updateTypeMeta(eventType: string, kind?: TrialTrackerUpdateKind) {
  if (kind === 'readout') return READOUT_UPDATE_META;
  return TYPE_META[eventType as TrialTrackerEventType] || {
    label: eventType.replace(/_/g, ' '),
    glyph: '•',
    bg: 'bg-tracker-defaultBg',
    fg: 'text-tracker-defaultFg',
    cat: 'status',
  };
}

export const CATEGORY_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New trials' },
  { key: 'results', label: 'Results' },
  { key: 'enroll', label: 'Enrollment' },
  { key: 'status', label: 'Status' },
  { key: 'dates', label: 'Dates' },
  { key: 'design', label: 'Design' },
  { key: 'sponsor', label: 'Sponsor' },
];

/** CT.gov overall_status values for sidebar trial-status filtering. */
export const TRIAL_STATUS_FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'RECRUITING', label: 'Recruiting' },
  { key: 'NOT_YET_RECRUITING', label: 'Not yet recruiting' },
  { key: 'ACTIVE_NOT_RECRUITING', label: 'Active, not recruiting' },
  { key: 'ENROLLING_BY_INVITATION', label: 'Enrolling by invitation' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'TERMINATED', label: 'Terminated' },
  { key: 'WITHDRAWN', label: 'Withdrawn' },
  { key: 'SUSPENDED', label: 'Suspended' },
];

export const DEFAULT_TRIAL_STATUS_FILTERS = ['all'];

export function normalizeTrialStatus(status?: string | null): string {
  return (status || '').trim().toUpperCase().replace(/\s+/g, '_');
}

/** Toggle a chip in a multi-select filter. "all" clears to show everything. */
export function toggleMultiFilter(current: string[], key: string, allKey = 'all'): string[] {
  if (key === allKey) return [allKey];
  const withoutAll = current.filter((k) => k !== allKey);
  if (withoutAll.includes(key)) {
    const next = withoutAll.filter((k) => k !== key);
    return next.length === 0 ? [allKey] : next;
  }
  return [...withoutAll, key];
}

export function isMultiFilterSelected(selected: string[], key: string, allKey = 'all'): boolean {
  if (key === allKey) return selected.length === 0 || selected.includes(allKey);
  return selected.includes(key);
}

export function isMultiFilterUnrestricted(selected: string[], allKey = 'all'): boolean {
  return selected.length === 0 || selected.includes(allKey);
}

export function parseSavedMultiFilter(raw: string | null | undefined, fallback: string[] = ['all']): string[] {
  if (!raw || !String(raw).trim()) return fallback;
  const parts = String(raw).split(',').map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts : fallback;
}

export function serializeMultiFilter(selected: string[]): string {
  return selected.join(',');
}

export function matchesTrialStatusFilter(
  status?: string | null,
  filterKeys?: string | string[] | null,
): boolean {
  const keys = Array.isArray(filterKeys) ? filterKeys : (filterKeys ? [filterKeys] : ['all']);
  if (isMultiFilterUnrestricted(keys)) return true;
  const normalized = normalizeTrialStatus(status);
  return keys.some((k) => normalizeTrialStatus(k) === normalized);
}

export const DESIGNATION_META: Record<string, { short: string; full: string; agency: string }> = {
  BTD: { short: 'Breakthrough', full: 'Breakthrough Therapy', agency: 'FDA' },
  ACCEL: { short: 'Accelerated', full: 'Accelerated Approval', agency: 'FDA' },
  FTD: { short: 'Fast Track', full: 'Fast Track', agency: 'FDA' },
  PRIORITY: { short: 'Priority Review', full: 'Priority Review', agency: 'FDA' },
  ODD: { short: 'Orphan Drug', full: 'Orphan Drug Designation', agency: 'FDA' },
  PRIME: { short: 'PRIME', full: 'PRIME priority medicine', agency: 'EMA' },
  CN_BTD: { short: 'NMPA Breakthrough', full: 'Breakthrough Therapy', agency: 'NMPA' },
  CN_PRIORITY: { short: 'NMPA Priority', full: 'Priority Review', agency: 'NMPA' },
};

export function registryForNctId(nctId?: string | null): { label: string; dot: string; bg: string; fg: string } {
  if (nctId && /^NCT/i.test(nctId)) {
    return { label: 'CT.gov', dot: 'bg-tracker-ctgovDot', bg: 'bg-tracker-ctgovBg', fg: 'text-tracker-ctgovFg' };
  }
  if (nctId && /^ChiCTR/i.test(nctId)) {
    return { label: 'ChiCTR', dot: 'bg-tracker-chictrDot', bg: 'bg-tracker-chictrBg', fg: 'text-tracker-chictrFg' };
  }
  return {
    label: nctId ? nctId.replace(/[0-9].*$/, '') || 'Registry' : 'Registry',
    dot: 'bg-slate-400',
    bg: 'bg-tracker-defaultBg',
    fg: 'text-tracker-defaultFg',
  };
}

export function nctRegistryUrl(nctId?: string | null): string | null {
  const id = (nctId || '').trim();
  if (!id) return null;
  if (/^NCT/i.test(id)) return `https://clinicaltrials.gov/study/${encodeURIComponent(id)}`;
  return null;
}

export function statusColorClasses(status?: string | null): { bg: string; fg: string } {
  const s = status || '';
  if (/recruit/i.test(s)) return { bg: 'bg-tracker-recruitingBg', fg: 'text-tracker-recruitingFg' };
  if (/active/i.test(s)) return { bg: 'bg-tracker-activeBg', fg: 'text-tracker-activeFg' };
  if (/complet/i.test(s)) return { bg: 'bg-tracker-completedBg', fg: 'text-tracker-completedFg' };
  if (/terminat|suspend|withdraw/i.test(s)) return { bg: 'bg-tracker-terminatedBg', fg: 'text-tracker-terminatedFg' };
  return { bg: 'bg-tracker-defaultBg', fg: 'text-tracker-defaultFg' };
}

export function relativeTimeLabel(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 6e4);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Calendar date for registry change timestamps (CT.gov submitted/posted dates). */
export function formatScrapeDate(iso?: string | null): string {
  if (!iso) return '—';
  const raw = String(iso).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return raw || '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export { parseTimelineDate, addMonths, phaseShortLabel, formatTimelineDate };

/** Regulatory-projection "what if" assumptions — ephemeral, localStorage-only (same posture as
 * ongoingTrialsTimeline's loadTimelineAssumptions), never persisted server-side. */
export interface TrialTrackerAssumptions {
  pcToFilingMonths: number;
  euFilingOffsetMonths: number;
  standardReviewMonths: number;
  priorityReviewMonths: number;
  euReviewMonths: number;
}

export const DEFAULT_TRACKER_ASSUMPTIONS: TrialTrackerAssumptions = {
  pcToFilingMonths: 6,
  euFilingOffsetMonths: 3,
  standardReviewMonths: 12,
  priorityReviewMonths: 8,
  euReviewMonths: 14,
};

const TRACKER_ASSUMPTIONS_KEY = 'ruby_trial_tracker_assumptions';

export function loadTrackerAssumptions(): TrialTrackerAssumptions {
  try {
    const raw = localStorage.getItem(TRACKER_ASSUMPTIONS_KEY);
    if (!raw) return { ...DEFAULT_TRACKER_ASSUMPTIONS };
    const parsed = JSON.parse(raw) as Partial<TrialTrackerAssumptions>;
    return { ...DEFAULT_TRACKER_ASSUMPTIONS, ...parsed };
  } catch {
    return { ...DEFAULT_TRACKER_ASSUMPTIONS };
  }
}

export function saveTrackerAssumptions(assumptions: TrialTrackerAssumptions) {
  try {
    localStorage.setItem(TRACKER_ASSUMPTIONS_KEY, JSON.stringify(assumptions));
  } catch {
    // localStorage unavailable — assumptions just won't persist across sessions
  }
}

export interface TrackerMilestones {
  start: Date | null;
  primaryCompletion: Date | null;
  studyCompletion: Date | null;
  usFiling: Date | null;
  usApproval: Date | null;
  euFiling: Date | null;
  euApproval: Date | null;
  isCompleted: boolean;
}

function isTrialCompletedStatus(status?: string | null): boolean {
  return /completed/i.test(status || '') && !/not yet completed/i.test(status || '');
}

/** US/EU filing + approval projections from primary completion, per the tracker's richer dual-lane
 * model (the mock's "PROJECTION ASSUMPTIONS" panel) — distinct from ongoingTrialsTimeline's
 * single-lane computeTrialMilestones, which this intentionally does not reuse. */
export function computeTrackerMilestones(
  trial: Pick<TrialTrackerTrial, 'study_start_date' | 'primary_completion_date' | 'study_completion_date' | 'status'>,
  assumptions: TrialTrackerAssumptions,
  isPriorityReview: boolean,
): TrackerMilestones {
  const start = parseTimelineDate(trial.study_start_date);
  const primaryCompletion = parseTimelineDate(trial.primary_completion_date);
  const studyCompletion = parseTimelineDate(trial.study_completion_date);
  const isCompleted = isTrialCompletedStatus(trial.status);

  const usFiling = !isCompleted && primaryCompletion
    ? addMonths(primaryCompletion, assumptions.pcToFilingMonths)
    : null;
  const usReviewMonths = isPriorityReview ? assumptions.priorityReviewMonths : assumptions.standardReviewMonths;
  const usApproval = usFiling ? addMonths(usFiling, usReviewMonths) : null;
  const euFiling = usFiling ? addMonths(usFiling, assumptions.euFilingOffsetMonths) : null;
  const euApproval = euFiling ? addMonths(euFiling, assumptions.euReviewMonths) : null;

  return { start, primaryCompletion, studyCompletion, usFiling, usApproval, euFiling, euApproval, isCompleted };
}

export function similarAssetsKey(target?: string | null, phase?: string | null): string {
  return `${(target || '').toLowerCase()}::${phaseShortLabel(phase || undefined)}`;
}
